import { Menu, Modal } from '@mantine/core'
import { DealsModal } from 'app/views/pages/deals-management/deals/deals-modal'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import MultiPartForm from 'app/views/pages/accounts-management/entities/multipart-form-modal'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { UpdatedFacilitiesScreen } from 'app/views/pages/deals-management/facilities/updated-facilities-screen'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { loadCalendars } from 'app/state/ducks/holiday-calendars/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks'
import { useNavigate } from 'react-router-dom'
import PrimaryInput from '../../buttons/PrimaryInput'
import IonIcon from '@reacticons/ionicons'
import { loadAllCompanies } from 'app/state/ducks/companies/thunks'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'
import { ErrorNotification } from 'app/views/components/notifications/notification'

// The lookup data each form's dropdowns read, and whether it has been fetched
// successfully at least once in this session.
//
// `loaded` lives at module level, not in state: this button sits in the page
// shell, which remounts on every top-level navigation, while the store it
// fills survives that remount. Component state would forget on every tab
// switch and show the loader again over data that is already there.
//
// It is a flag rather than a check for non-empty lists because an empty list
// does not mean "not fetched yet" — a tenant with no contacts or no wire
// instructions is legitimately empty, and a length check would send it down
// the blocking-loader path on every open, permanently.
type FormLookups = {
  loaded: boolean
  loads: Array<() => unknown>
}

const dealAndFacilityForm: FormLookups = {
  loaded: false,
  // The deal stepper embeds the facility form, so both modals need the same set.
  loads: [
    loadAllDeals,
    loadAllFacilities,
    loadEntities,
    loadContacts,
    loadCalendars,
    loadWireInstructions,
    loadIndexRateOptions,
    loadAllEntityProfiles,
  ],
}

const entityForm: FormLookups = {
  loaded: false,
  loads: [
    loadEntities,
    loadContacts,
    loadWireInstructions,
    loadAllEntityProfiles,
    loadAllEntityTypes,
    loadAllCompanies,
  ],
}

/**
 * Loads a form's lookup data for its first open in the session, and reports
 * whether the modal should show a loader while it does.
 *
 * Every thunk is coalesced, so overlapping opens share requests. The thunks do
 * not report failure alike: most swallow errors and resolve with `[]` or
 * `undefined`, `loadAllCompanies` resolves with `{ success: false }` — or
 * rejects outright on a network failure, where its catch reads `e.response`.
 * So every lookup is allowed to settle (`allSettled`, never an early reject
 * that would drop the loader while other requests are still pending), and the
 * open counts as loaded only when EVERY lookup fulfilled with a dispatched
 * action — the one shape all of them return on success and none on failure.
 *
 * On failure the gate stays up and the user is told; closing and reopening
 * retries. Known limit: the fan-out thunks (entities, contacts, wire
 * instructions, deals, facilities, IRO, calendars) run two or three requests
 * each, and their child loaders catch and return `[]`, so losing ONE child
 * still yields a normal action over a partial list, which this gate cannot
 * see. Closing that needs the thunks to report completeness themselves — a
 * change to every caller of those thunks, kept out of this button on purpose.
 *
 * Once loaded, later opens do nothing here. The store is kept current by the
 * pages that own each list and by the create reducers themselves, so a warm
 * open costs no requests at all (a refresh here re-fired every lookup on every
 * open, which is what this button exists to stop).
 */
const isDispatchedAction = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { type?: unknown }).type === 'string'

async function loadLookups(
  form: FormLookups,
  dispatch: ReturnType<typeof useDispatch>,
  setLoading: (loading: boolean) => void,
): Promise<void> {
  if (form.loaded) return

  setLoading(true)
  const results = await Promise.allSettled(
    form.loads.map(load => dispatch(load() as any) as Promise<unknown>),
  )
  const allLoaded = results.every(
    r => r.status === 'fulfilled' && isDispatchedAction(r.value),
  )
  if (!allLoaded) {
    // Gate stays up; `loaded` stays false so the next open retries.
    ErrorNotification({
      title: 'Could not load form data',
      message: 'Close this dialog and try again.',
    })
    return
  }
  form.loaded = true
  setLoading(false)
}

/**
 * Runs the form's first-open load when `open` flips to true, and returns
 * whether the modal should show a loader.
 *
 * `loading` is state of whichever small component calls this — never of
 * CreateNewButton. A loader clearing for the Entity form must re-render only
 * the Entity modal: if the flag lived in the button, `setLoading(false)` could
 * fire after the user had closed Entity and started typing in Deal, re-render
 * the button and with it the open Deal modal, remount its inline stepper
 * steps, and wipe the form (see memory:
 * create-new-steppers-remount-on-parent-rerender).
 */
function useLookupGate(open: boolean, form: FormLookups): boolean {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  // Once per open transition: `open` is the only value that changes here, so
  // a re-render while the modal stays open cannot re-trigger the load.
  useEffect(() => {
    if (open) void loadLookups(form, dispatch, setLoading)
  }, [open, form, dispatch])
  return loading
}

type OpenProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

function DealCreate({ open, setOpen }: OpenProps) {
  const loading = useLookupGate(open, dealAndFacilityForm)
  return (
    <DealsModal
      open={open}
      setOpen={setOpen}
      useStepper={true}
      loading={loading}
    />
  )
}

function FacilityCreate({ open, setOpen }: OpenProps) {
  const loading = useLookupGate(open, dealAndFacilityForm)
  return (
    <Modal
      className="modal-body modal-body-template fit-content-modal"
      opened={open}
      onClose={() => setOpen(false)}
      centered={true}
      size="77%"
    >
      {loading ? (
        <CustomLoaderPage />
      ) : (
        <UpdatedFacilitiesScreen
          deal={undefined}
          facility={undefined}
          onClose={() => setOpen(false)}
        />
      )}
    </Modal>
  )
}

function EntityCreate({ open, setOpen }: OpenProps) {
  const loading = useLookupGate(open, entityForm)
  if (!open) return null
  return <MultiPartForm setOpen={setOpen} open={open} loading={loading} />
}

export function CreateNewButton() {
  const navigate = useNavigate()

  const [openDeal, setOpenDeal] = useState(false)
  const [openFacility, setOpenFacility] = useState(false)
  const [openEntity, setOpenEntity] = useState(false)

  // Lookup data is fetched when a menu item is clicked, not on mount. This
  // button lives in the page shell, which remounts on every top-level
  // navigation, so a mount-time load refired on every tab switch (10 loaders,
  // 19 requests on this branch, whether or not the page needed any of it).
  //
  // This component holds only the three `open` flags. Loading state lives in
  // each *Create component below (useLookupGate), so nothing that settles
  // asynchronously re-renders here.
  return (
    <>
      <Menu shadow="md" width={200} position="bottom-start">
        <Menu.Target>
          <div style={{ width: 'fit-content' }}>
            <PrimaryInput w="159px">
              Create New
              <IonIcon
                name="chevron-down-outline"
                className="primaryInputChevronDown"
              />
            </PrimaryInput>
          </div>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item onClick={() => setOpenDeal(true)}> Deal </Menu.Item>
          <Menu.Item onClick={() => setOpenFacility(true)}>Facility</Menu.Item>
          <Menu.Item onClick={() => setOpenEntity(true)}> Entity</Menu.Item>
          <Menu.Item
            onClick={() => {
              navigate('/tradeentry/create', {
                state: { tab: 'tradeEntryCreation' },
              })
            }}
          >
            {' '}
            Trade
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <DealCreate open={openDeal} setOpen={setOpenDeal} />
      <EntityCreate open={openEntity} setOpen={setOpenEntity} />
      <FacilityCreate open={openFacility} setOpen={setOpenFacility} />
    </>
  )
}
